import clsx from "clsx"
import { FC, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { getCurrencyLabel } from "@/helpers/getCurrencyLabel"
import { useAppDispatch, useAppSelector } from "@/store"
import { STORE_CURRENCY } from "@/store/apis/storePaymentsAPI"
import { AppSelectors, setStoreCurrency } from "@/store/slices/app"
import { SwitchInline } from "@/ui/SwitchInline"
import styles from "../../Shop.module.scss"
import type { IShopCurrencySwitch } from "./types"

export const ShopCurrencySwitch: FC<IShopCurrencySwitch> = ({
  currencies,
  className,
  ...rest
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const storeCurrency = useAppSelector(AppSelectors.selectStoreCurrency())
  const isTMA = useAppSelector(AppSelectors.selectIsTMA())

  const availableCurrencies = useMemo(
    () =>
      isTMA
        ? currencies
        : currencies.filter((c) => c.currency !== STORE_CURRENCY.TG_STARS),
    [isTMA, currencies]
  )

  const isCurrencyAvailable = availableCurrencies.some(
    (c) => c.currency === storeCurrency
  )
  const fallbackCurrency = availableCurrencies[0]?.currency
  const selectedCurrency = isCurrencyAvailable
    ? storeCurrency
    : fallbackCurrency

  // One-time correction: if the persisted `storeCurrency` is no longer in
  // the available set (e.g. user toggled isTMA or switched tabs), push the
  // fallback into Redux. This is NOT a sync effect for derived state —
  // it only fires on mount / when the persisted value becomes invalid.
  useEffect(() => {
    if (!isCurrencyAvailable && fallbackCurrency) {
      dispatch(setStoreCurrency(fallbackCurrency))
    }
  }, [isCurrencyAvailable, fallbackCurrency, dispatch])

  const initSelect = availableCurrencies.findIndex(
    (c) => c.currency === selectedCurrency
  )

  return (
    <SwitchInline
      {...rest}
      className={clsx(styles.Shop_currencySwitch, className)}
      initSelect={Math.max(initSelect, 0)}
    >
      {availableCurrencies.map((option) => {
        const Icon = option.icon
        return (
          <SwitchInline.Item
            key={option.currency}
            className={styles.Shop_currencySwitch_item}
            onClick={() => dispatch(setStoreCurrency(option.currency))}
          >
            <Icon /> {getCurrencyLabel(t, option.currency)}
          </SwitchInline.Item>
        )
      })}
    </SwitchInline>
  )
}
